"use client";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { MouseEventHandler, ReactNode } from "react";

interface BackButtonBaseProps {
  className?: string;
  ariaLabel?: string;
  children?: ReactNode;
}

type BackButtonProps = BackButtonBaseProps &
  (
    | {
        fallbackPath?: string;
        /** ルート障害からの復帰など、画面全体の再読み込みが必要な場合。 */
        reloadDocument?: boolean;
        onClick?: never;
        disabled?: never;
      }
    | {
        /** 未保存確認や入力ステップの復帰処理を、そのまま呼び出す。 */
        onClick: MouseEventHandler<HTMLButtonElement>;
        disabled?: boolean;
        fallbackPath?: never;
        reloadDocument?: never;
      }
  );

/** 戻る導線は、矢印とラベルを組み合わせた枠線付きの1種類に統一する。 */
export function BackButton({
  fallbackPath = "/",
  className,
  ariaLabel,
  children,
  onClick,
  disabled,
  reloadDocument,
}: BackButtonProps) {
  const label =
    children == null ||
    typeof children === "boolean" ||
    (typeof children === "string" && children.trim() === "")
      ? "戻る"
      : children;

  const buttonClassName = cn(
    // 共通Buttonの標準高（36px）に揃える。長い戻り先だけは折り返して広げる。
    "text-brand-strong hover:bg-brand-light hover:text-brand-strong dark:hover:bg-brand-light focus-visible:ring-brand-strong/60 h-auto min-h-9 max-w-full justify-start whitespace-normal py-1.5 text-left transition-colors",
    className
  );
  const content = (
    <>
      <span
        aria-hidden="true"
        className="bg-brand-light flex size-5 shrink-0 items-center justify-center rounded-full"
      >
        <ArrowLeft className="size-4" />
      </span>
      <span className="min-w-0 [overflow-wrap:anywhere]">{label}</span>
    </>
  );

  if (onClick) {
    return (
      <Button
        type="button"
        variant="outline"
        data-slot="back-button"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        className={buttonClassName}
      >
        {content}
      </Button>
    );
  }

  return (
    <Button
      asChild
      variant="outline"
      data-slot="back-button"
      className={buttonClassName}
    >
      {reloadDocument ? (
        <a href={fallbackPath} aria-label={ariaLabel}>
          {content}
        </a>
      ) : (
        <Link to={fallbackPath} aria-label={ariaLabel}>
          {content}
        </Link>
      )}
    </Button>
  );
}
